import { useEffect, useState } from "react";
import { X } from "../../pages/overview/icons";
import {
  SnippetContextProvider,
  useSnippetContext,
} from "../../pages/docs/snippetContext";
import { ConnectStep } from "../../pages/welcome/ConnectStep";
import {
  CLI_COMMAND_LEAD,
  CliCommandBlock,
} from "../../pages/welcome/CliCommandBlock";
import { StepBody } from "../../pages/welcome/StepBody";
import { WizardCta } from "../../pages/welcome/WizardCta";
import { cliRouteOffered } from "../../pages/welcome/cliHandoff";
import { cliHandoffCommand } from "../../pages/welcome/cliHandoffCommand";
import { useSnippetKey } from "../../pages/welcome/useSnippetKey";
import { useWorkspace } from "../../workspaces";
import { trackButtonPressed } from "../../analytics/tracking";
import type { AgentKind } from "../../agents";

/* Reconnect drawer — a slim variant of the Add-Agent drawer that skips the
 * set-up / verify steps and shows only the connection instructions for an
 * already-registered agent (opened from the agent detail header's "Reconnect").
 *
 * A key value is only returned once, at creation, so re-showing a complete
 * snippet means minting a fresh workspace API key. We mint one when the reader
 * reaches the snippet and feed it to ConnectStep via SnippetContext. The CLI
 * route mints nothing here; the CLI creates its own key. */
export function ReconnectDrawer({
  open,
  onClose,
  agentId,
  kind,
}: {
  open: boolean;
  onClose: () => void;
  agentId: string;
  kind: AgentKind;
}) {
  // Close on Escape while open.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Scrim — not clickable to close, same as the Add-Agent wizard: the key
          minted on open is only shown here once, so an accidental backdrop
          click would strand it. Closing is the X button or Escape. */}
      <div
        aria-hidden
        className="fixed inset-0 z-[60] bg-black/30 transition-opacity duration-200"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Reconnect agent"
        aria-hidden={!open}
        className="tw-app tw-app-dark fixed inset-y-0 right-0 z-[61] flex w-[520px] max-w-full flex-col"
        style={{
          background: "#280611",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 320ms cubic-bezier(0.22,1,0.36,1)",
          boxShadow: open ? "-14px 0 36px rgba(0,0,0,0.13)" : "none",
          borderLeft: "1px solid var(--border-2)",
          visibility: open ? "visible" : "hidden",
        }}
      >
        <div
          className="flex flex-shrink-0 items-center justify-between px-7 py-3.5"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div>
            <h2 className="tw-display text-[14px] font-semibold text-[var(--text)]">
              Reconnect agent
            </h2>
            <p className="mt-0.5 text-[10px] text-[var(--text-muted)]">
              Point this agent&apos;s traffic at the gateway with a fresh key.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Only mount while open so a key is minted at most once per open (and
            not in the background). Pin the snippet context to this agent. */}
        {open && (
          <SnippetContextProvider agentId={agentId}>
            <div
              className="onboarding ai21-brand flex min-h-0 flex-1 flex-col"
              style={{ fontFamily: "var(--ai21-font-ui)" }}
            >
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <ReconnectBody agentId={agentId} kind={kind} onDone={onClose} />
              </div>
            </div>
          </SnippetContextProvider>
        )}
      </aside>
    </>
  );
}

/* Picks the route. The CLI route mints nothing: the CLI signs the reader in and creates
 * its own key. Only the snippet needs one from us, so the mint lives in SnippetReconnect
 * and runs only when the reader lands there. */
function ReconnectBody({
  agentId,
  kind,
  onDone,
}: {
  agentId: string;
  kind: AgentKind;
  onDone: () => void;
}) {
  const { workspace } = useWorkspace();
  const { userId } = useSnippetContext();

  /* The CLI is the offered route and the snippet is the fallback, same order as the
   * creation wizard: one command reconnects the repo and proves it, where the snippet
   * asks the reader to place JSON correctly and then trust that it worked. Set by the
   * reader choosing "Configure manually instead" — never by us on their behalf. */
  const [manual, setManual] = useState(false);

  /* Renders nothing CLI-shaped unless the CLI can actually configure this kind — it
   * only writes Claude Code's and Codex's config, so offering it for a LangChain agent
   * would hand over a command that configures the wrong client. Those readers go
   * straight to the snippet, as before. */
  const cliRoute = cliRouteOffered(kind, agentId);

  if (cliRoute && !manual) {
    return (
      <CliReconnect
        command={cliHandoffCommand({
          agentId,
          userId,
          workspaceId: workspace.id,
          kind,
        })}
        onDone={onDone}
        onManual={() => setManual(true)}
      />
    );
  }

  return (
    <SnippetReconnect
      agentId={agentId}
      kind={kind}
      onDone={onDone}
      onUseCli={cliRoute ? () => setManual(false) : undefined}
    />
  );
}

/* Mints a fresh key on mount and renders the snippet once it resolves. Lives under
 * SnippetContextProvider so it can feed the new key in; a key already there (from an
 * earlier trip to the snippet this open) is reused rather than minted again. */
function SnippetReconnect({
  agentId,
  kind,
  onDone,
  onUseCli,
}: {
  agentId: string;
  kind: AgentKind;
  onDone: () => void;
  /** Back to the command, for kinds that were offered it. */
  onUseCli?: () => void;
}) {
  const state = useSnippetKey(agentId, `${agentId} reconnect key`);

  return (
    <div className="flex flex-col gap-4">
      {onUseCli && (
        <button
          type="button"
          onClick={onUseCli}
          className="self-start t-caption transition-colors"
          style={{ color: "var(--onb-link)", fontWeight: 600 }}
        >
          ← Use the CLI instead
        </button>
      )}
      {state === "error" && (
        <p className="t-body text-[13px]" style={{ color: "var(--fg-2)" }}>
          Couldn&apos;t mint a new key. Close and try again, or create one from
          Settings › API keys.
        </p>
      )}
      {state === "minting" && (
        <p className="t-body text-[13px]" style={{ color: "var(--fg-3)" }}>
          Minting a fresh key…
        </p>
      )}
      {state === "ready" && (
        <ConnectStep
          kind={kind}
          agentId={agentId}
          onContinue={onDone}
          standalone
          ctaLabel="Done"
        />
      )}
    </div>
  );
}

/* What the drawer leads with: reconnecting is one command, so the snippet waits behind
 * a link rather than competing with it. */
function CliReconnect({
  command,
  onDone,
  onManual,
}: {
  command: string;
  onDone: () => void;
  onManual: () => void;
}) {
  return (
    <StepBody title="Reconnect from your terminal" lead={CLI_COMMAND_LEAD}>
      <CliCommandBlock command={command} />

      {/* No traffic-based confirmation here, unlike the wizard: `used` only says the
          agent has *ever* sent traffic, and a reconnected agent always has. */}
      <p className="text-[11px] text-[var(--text-muted)]">
        The CLI verifies the connection and tells you there.
      </p>

      <WizardCta
        className="w-full"
        onClick={() => {
          trackButtonPressed("Done");
          onDone();
        }}
      >
        Done
      </WizardCta>

      {/* The way out for anyone not using the CLI. Same classes as the wizard's
          opt-out (CliHandoffStep) — the same choice in the same position. */}
      <button
        type="button"
        onClick={onManual}
        className="text-body-sm underline text-fg-2"
      >
        Configure manually instead
      </button>
    </StepBody>
  );
}
